/**
 * =============================================================================
 * SECTION HEADER COMPONENT - AI-NATIVE DESIGN SYSTEM
 * =============================================================================
 * 
 * Consistent section headers across all views.
 * 
 * DESIGN SYSTEM:
 * - Section title: 18px semibold
 * - Count badge: 14px muted
 * - Optional severity indicator
 * - 8pt spacing grid
 */

import { Box, Group, Stack, Text } from '@mantine/core';
import { memo, type FC, type ReactNode } from 'react';

import { spacing, typography, neutralColors, semanticColors, severityIndicator } from '@styles/design-system';

type Severity = 'critical' | 'warning' | 'positive' | 'info';

export interface SectionHeaderProps {
    /** Section title - 18px */
    title: string;
    /** Optional subtitle - 13px muted */
    subtitle?: string;
    /** Optional count to display */
    count?: number;
    /** Semantic severity indicator */
    severity?: Severity;
    /** Right-side actions */
    actions?: ReactNode;
}

const SectionHeader: FC<SectionHeaderProps> = memo(({ 
    title, 
    subtitle,
    count,
    severity,
    actions 
}) => {
    const severityColor = severity 
        ? semanticColors[severity].primary 
        : undefined;

    return (
        <Group 
            justify="space-between" 
            align="center"
            style={{ marginBottom: spacing.sm }}
        >
            <Group style={{ gap: spacing.sm }}>
                {/* Severity indicator bar */}
                {severity && (
                    <Box 
                        style={{ 
                            width: severityIndicator.width, 
                            height: 24, 
                            borderRadius: 2,
                            backgroundColor: severityColor 
                        }} 
                    />
                )}
                
                <Stack style={{ gap: 2 }}>
                    <Group style={{ gap: spacing.xs }}>
                        {/* Section title - 18px */}
                        <Text 
                            style={{
                                ...typography.sectionHeader,
                                color: neutralColors.textPrimary,
                            }}
                        >
                            {title}
                        </Text>
                        
                        {/* Count */}
                        {count !== undefined && count > 0 && (
                            <Text 
                                style={{
                                    ...typography.body,
                                    fontWeight: 500,
                                    color: neutralColors.textMuted,
                                }}
                            >
                                ({count})
                            </Text>
                        )}
                    </Group>
                    
                    {/* Subtitle */}
                    {subtitle && (
                        <Text 
                            style={{
                                ...typography.secondary,
                                color: neutralColors.textMuted,
                            }}
                        >
                            {subtitle}
                        </Text>
                    )}
                </Stack>
            </Group>
            
            {/* Actions */}
            {actions}
        </Group>
    );
});

SectionHeader.displayName = 'SectionHeader';

export default SectionHeader;
